"use client";

import { useState, type ReactNode } from "react";

/**
 * Interactive figures for "How memories become knowledge" (agent-memory part 3
 * — Neocortex). Same split as part 2: concepts that are variable RELATIONSHIPS
 * you grasp by moving an input are playable; everything else stays static.
 *
 * FigBandLive — the Luhn band with three selectable fates: a thing mentioned
 * once, a thing that comes up sometimes, a thing in almost every conversation.
 * weight(n) = ln(N/n) inside the band [2, ceil(0.10·N)], zero outside —
 * mirrors buildCommunities (DF_FLOOR, the 10% df-ceiling, the idf line); the
 * validator asserts the mirrored constants. Drawn for a realistically sized
 * window (N = 200): the post's 20-moment demo window has a degenerate
 * one-value band, which is accurate but teaches nothing to explore.
 */

const C = {
  signal: "var(--color-signal)",
  signalDeep: "var(--color-signal-deep)",
  axis: "var(--color-border)",
  text: "var(--color-foreground-muted)",
  faint: "var(--color-foreground-faint)",
  strong: "var(--color-foreground-strong)",
  surface: "var(--color-surface)",
};

function Frame({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <figure className="overflow-hidden rounded-xl border border-border-subtle bg-surface-elevated">
      <div className="p-5 sm:p-6">{children}</div>
      <figcaption className="border-t border-border-subtle px-5 py-3 font-mono text-xs leading-relaxed tracking-[0.03em] text-foreground-muted">
        {caption}
      </figcaption>
    </figure>
  );
}

/* Mirrors communities.ts: DF_FLOOR = 2, entity-mode df-ceiling = 10% of the
 * window, idf = ln(window / count). */
const WINDOW = 200;
const FLOOR = 2;
const CEILING = Math.ceil(WINDOW * 0.1);
const weightFor = (n: number): number =>
  n < FLOOR || n > CEILING ? 0 : Math.log(WINDOW / n);

const X0 = 36, X1 = 444, Y_BASE = 150, Y_TOP = 44;
const MAX_W = Math.log(WINDOW / FLOOR);
const xFor = (n: number): number => X0 + (Math.log(n) / Math.log(WINDOW)) * (X1 - X0);
const yFor = (w: number): number => Y_BASE - (w / MAX_W) * (Y_BASE - Y_TOP);

const CURVE = Array.from({ length: CEILING - FLOOR + 1 }, (_, i) => FLOOR + i)
  .map((n, i) => `${i === 0 ? "M" : "L"}${xFor(n).toFixed(1)} ${yFor(weightFor(n)).toFixed(1)}`)
  .join(" ");

/* The same idf line continued through the cut-off zones as a ghost — what the
 * weight WOULD be without Luhn's cuts (on the log-x axis it stays a straight
 * line, so two points per segment suffice). The dot still drops to the floor
 * outside the band; the ghost shows the cut is a decision, not the curve's end. */
const uncut = (n: number): number => Math.log(WINDOW / n);
const ghost = (a: number, b: number): string =>
  `M${xFor(a).toFixed(1)} ${yFor(uncut(a)).toFixed(1)} L${xFor(b).toFixed(1)} ${yFor(uncut(b)).toFixed(1)}`;
const GHOST_LEFT = ghost(1, FLOOR);
const GHOST_RIGHT = ghost(CEILING, WINDOW);

type PresetKey = "once" | "sometimes" | "always";
const PRESETS: readonly { key: PresetKey; label: string; thing: string; n: number }[] = [
  { key: "once", label: "mentioned once", thing: "a laptop charger", n: 1 },
  { key: "sometimes", label: "comes up sometimes", thing: "Lisbon", n: 6 },
  { key: "always", label: "in almost everything", thing: "Maya", n: 195 },
];

export function FigBandLive() {
  const [key, setKey] = useState<PresetKey>("sometimes");
  const preset = PRESETS.find((p) => p.key === key) ?? PRESETS[1];
  const n = preset.n;
  const w = weightFor(n);
  const inBand = n >= FLOOR && n <= CEILING;
  const state =
    n < FLOOR ? "only once · nothing to tie"
    : n > CEILING ? "too common · set aside"
    : `weight ${w.toFixed(1)}`;
  return (
    <Frame caption="What a name is worth depends on how often it is mentioned. Only once and there is nothing to tie. In almost everything and it would tie everything, so it is set aside. In between, the rarer the name, the heavier it counts.">
      <svg viewBox="0 0 480 200" className="h-auto w-full" role="img" aria-label="Weight by mention count, with hatched set-aside zones on both ends and descending weight between them">
        <defs>
          <pattern id="bandHatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="7" stroke={C.axis} strokeWidth="1" />
          </pattern>
        </defs>
        <rect x={X0} y={Y_TOP - 14} width={xFor(FLOOR) - X0} height={Y_BASE - Y_TOP + 14} fill="url(#bandHatch)" opacity="0.5" />
        <rect x={xFor(CEILING)} y={Y_TOP - 14} width={X1 - xFor(CEILING)} height={Y_BASE - Y_TOP + 14} fill="url(#bandHatch)" opacity="0.5" />
        <rect x={xFor(FLOOR)} y={Y_TOP - 14} width={xFor(CEILING) - xFor(FLOOR)} height={Y_BASE - Y_TOP + 14} fill={C.signal} fillOpacity="0.1" />
        <line x1={xFor(FLOOR)} y1={Y_TOP - 14} x2={xFor(FLOOR)} y2={Y_BASE} stroke={C.signal} strokeOpacity="0.4" strokeWidth="1" strokeDasharray="3 3" />
        <line x1={xFor(CEILING)} y1={Y_TOP - 14} x2={xFor(CEILING)} y2={Y_BASE} stroke={C.signal} strokeOpacity="0.4" strokeWidth="1" strokeDasharray="3 3" />
        <line x1={X0} y1={Y_BASE} x2={X1} y2={Y_BASE} stroke={C.axis} strokeWidth="1.5" />
        <path d={GHOST_LEFT} stroke={C.signal} strokeOpacity="0.3" strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
        <path d={GHOST_RIGHT} stroke={C.signal} strokeOpacity="0.3" strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
        <path d={CURVE} stroke={C.signal} strokeWidth="2" fill="none" />
        <circle
          cx={xFor(n)}
          cy={inBand ? yFor(w) : Y_BASE}
          r="6"
          fill={inBand ? C.signal : C.axis}
          style={{ transition: "cx 0.25s ease, cy 0.25s ease, fill 0.25s ease" }}
        />
        <text x={X0} y="20" fill={C.text} fontSize="11" className="font-mono">weight</text>
        <text x={(xFor(FLOOR) + xFor(CEILING)) / 2} y={Y_BASE - 8} textAnchor="middle" fill={C.signalDeep} fontSize="10" className="font-mono">kept</text>
        <text x={xFor(1)} y={Y_BASE + 18} textAnchor="start" fill={C.faint} fontSize="10" className="font-mono">once</text>
        <text x={X1} y={Y_BASE + 18} textAnchor="end" fill={C.faint} fontSize="10" className="font-mono">every moment</text>
        <text x={(X0 + X1) / 2} y="192" textAnchor="middle" fill={C.faint} fontSize="10" className="font-mono">mentioned in more of the window →</text>
      </svg>
      <div className="mt-3 flex flex-col items-start gap-2">
        <div role="group" aria-label="pick a thing" className="inline-flex flex-wrap overflow-hidden rounded-lg border border-border">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={p.key === key}
              onClick={() => setKey(p.key)}
              className={`border-r border-border-subtle px-3 py-2 text-left font-mono text-xs last:border-r-0 ${
                p.key === key ? "bg-ink text-cream" : "text-foreground-muted hover:bg-cream-warm"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="min-w-44 rounded-lg border border-border-subtle bg-surface px-3 py-2 font-mono text-xs">
          <span className="text-foreground-strong">{preset.thing} · {String(n)} of {String(WINDOW)}</span>
          <span className={`block ${inBand ? "text-signal-deep" : "text-foreground-muted"}`}>{state}</span>
        </div>
      </div>
    </Frame>
  );
}

/* FigWebLive — the joining game on a five-moment web drawn as the web is:
 * each node a MOMENT labeled by its things, ties where banded things are
 * shared, weight = the sum (window arithmetic: Lucia's/dinner/offsite in 2 of
 * 20 → 2.3 each; Lisbon in 3 of 20 → 1.9; Sara in 5 of 20 → 1.4). The Lisbon
 * side is a TRIANGLE — one moment can hold several ties, and a group of three
 * is three pairs agreeing. The bridge is a shared person (Sara).
 * The validator re-runs this graph through the REAL Louvain library and
 * asserts the two-thread verdict and the quoted luck-bars. */
